import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Share2, X, Copy, Check } from 'lucide-react';
import SmartCalculatorsSidebar from '../../components/common/SmartCalculatorsSidebar';
import Navbar from '../../components/common/Navbar';

const LOAN_CONFIG = {
  personal: {
    label: 'Personal Loan',
    amtMin: 50000, amtMax: 5000000, amtStep: 10000,
    rateMin: 8,  rateMax: 30,
    tenureMin: 1, tenureMax: 7,
    defaults: { amtStr: '500000', rateStr: '12', tenure: 3 },
    ratePresets: [11, 13, 15, 18],
  },
  home: {
    label: 'Home Loan',
    amtMin: 500000, amtMax: 50000000, amtStep: 100000,
    rateMin: 6, rateMax: 15,
    tenureMin: 1, tenureMax: 30,
    defaults: { amtStr: '5000000', rateStr: '8.5', tenure: 20 },
    ratePresets: [8, 8.5, 9, 9.5],
  },
  car: {
    label: 'Car Loan',
    amtMin: 100000, amtMax: 5000000, amtStep: 10000,
    rateMin: 7, rateMax: 20,
    tenureMin: 1, tenureMax: 7,
    defaults: { amtStr: '800000', rateStr: '9.5', tenure: 5 },
    ratePresets: [9, 10, 12, 14],
  },
};

const EMICalculator = () => {
  const [loanType, setLoanType]   = useState('personal');
  const [amtStr, setAmtStr]       = useState(LOAN_CONFIG.personal.defaults.amtStr);
  const [rateStr, setRateStr]     = useState(LOAN_CONFIG.personal.defaults.rateStr);
  const [tenure, setTenure]       = useState(LOAN_CONFIG.personal.defaults.tenure);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied]       = useState(false);
  const [amortView, setAmortView] = useState('yearly');

  const cfg        = LOAN_CONFIG[loanType];
  const loanAmount = parseInt(amtStr) || 0;
  const interestRate = Math.min(Math.max(parseFloat(rateStr) || cfg.rateMin, cfg.rateMin), cfg.rateMax);
  const sliderAmt  = Math.min(Math.max(loanAmount, cfg.amtMin), cfg.amtMax);

  const handleLoanTypeSwitch = (type) => {
    setLoanType(type);
    setAmtStr(LOAN_CONFIG[type].defaults.amtStr);
    setRateStr(LOAN_CONFIG[type].defaults.rateStr);
    setTenure(LOAN_CONFIG[type].defaults.tenure);
  };

  // ── EMI Calculation ────────────────────────────────────────────────────────
  const { emi, totalInterest, totalAmount } = useMemo(() => {
    const months = tenure * 12;
    if (loanAmount <= 0 || months <= 0) return { emi: 0, totalInterest: 0, totalAmount: 0 };
    const r = interestRate / 12 / 100;
    if (r === 0) {
      const e = Math.round(loanAmount / months);
      return { emi: e, totalInterest: 0, totalAmount: loanAmount };
    }
    const e     = Math.round(loanAmount * r * Math.pow(1 + r, months) / (Math.pow(1 + r, months) - 1));
    const total = e * months;
    return { emi: e, totalInterest: total - loanAmount, totalAmount: total };
  }, [loanAmount, interestRate, tenure]);

  // ── Amortization Schedule ──────────────────────────────────────────────────
  const { monthlySchedule, yearlySchedule } = useMemo(() => {
    if (emi <= 0 || loanAmount <= 0) return { monthlySchedule: [], yearlySchedule: [] };
    const r = interestRate / 12 / 100;
    const months = tenure * 12;
    const monthly = [];
    let balance = loanAmount;

    for (let m = 1; m <= months; m++) {
      const interest  = Math.round(balance * r);
      const principal = Math.min(emi - interest, balance);
      const closing   = Math.max(Math.round(balance - principal), 0);
      monthly.push({ month: m, opening: Math.round(balance), emi, principal, interest, closing });
      balance = closing;
    }

    const yearly = [];
    for (let y = 0; y < tenure; y++) {
      const slice = monthly.slice(y * 12, (y + 1) * 12);
      yearly.push({
        year:         y + 1,
        opening:      slice[0].opening,
        principal:    slice.reduce((s, m) => s + m.principal, 0),
        interest:     slice.reduce((s, m) => s + m.interest, 0),
        totalPayment: slice.reduce((s, m) => s + m.emi, 0),
        closing:      slice[slice.length - 1].closing,
      });
    }
    return { monthlySchedule: monthly, yearlySchedule: yearly };
  }, [emi, loanAmount, interestRate, tenure]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const fmt = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

  const fmtC = (n) => {
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
    return `₹${(n / 1000).toFixed(0)}K`;
  };

  const sliderBg = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return `linear-gradient(to right, #115e59 0%, #115e59 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`;
  };

  const handleShare = (platform) => {
    const text = encodeURIComponent('Calculate your loan EMI instantly with Kapitalised: kapitalised.com/calculators/emi');
    if (platform === 'copy') {
      navigator.clipboard.writeText('https://kapitalised.com/calculators/emi');
      setCopied(true); setTimeout(() => setCopied(false), 2000); return;
    }
    const maps = { whatsapp: `https://wa.me/?text=${text}`, twitter: `https://twitter.com/intent/tweet?text=${text}` };
    window.open(maps[platform], '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  const handleAmtInput  = (raw) => setAmtStr(raw.replace(/[^0-9]/g, ''));
  const handleRateInput = (raw) => setRateStr(raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'));
  const handleTenure    = (raw) => {
    const n = parseInt(String(raw).replace(/[^0-9]/g, '')) || cfg.tenureMin;
    setTenure(Math.min(Math.max(n, cfg.tenureMin), cfg.tenureMax));
  };

  // ── Donut chart math ───────────────────────────────────────────────────────
  const R = 70, CX = 100, CY = 100;
  const circ     = 2 * Math.PI * R;
  const gap      = 5;
  const prinPct  = totalAmount > 0 ? loanAmount / totalAmount : 0.5;
  const intPct   = totalAmount > 0 ? totalInterest / totalAmount : 0.5;
  const prinDash = Math.max(prinPct * circ - gap, 0);
  const intDash  = Math.max(intPct * circ - gap, 0);

  // ── Style tokens ───────────────────────────────────────────────────────────
  const labelSt    = { fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 500, color: '#374151' };
  const valueSt    = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 600, color: '#115e59', border: 'none', outline: 'none', background: 'transparent', textAlign: 'right' };
  const suffixSt   = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 500, color: '#9ca3af' };
  const minMaxSt   = { fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' };
  const inputBoxSt = { display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '6px 12px' };

  const amortData = amortView === 'yearly' ? yearlySchedule : monthlySchedule;

  const css = `
    .emi-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
    .emi-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
    .emi-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
    .no-spin::-webkit-outer-spin-button, .no-spin::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    .no-spin { -moz-appearance: textfield; }
    .emi-card { padding: 36px; }
    .emi-chart-container { flex-shrink: 0; }
    .emi-seo { background-color: #ffffff; padding: 48px 450px 64px 100px; }
    .emi-amort th { background: #115e59; color: #fff; font-family: Onest, sans-serif; font-size: 12px; font-weight: 600; padding: 11px 14px; text-align: right; white-space: nowrap; }
    .emi-amort th:first-child { text-align: left; }
    .emi-amort td { padding: 10px 14px; font-family: Onest, sans-serif; font-size: 13px; color: #374151; text-align: right; border-bottom: 1px solid #f3f4f6; white-space: nowrap; }
    .emi-amort td:first-child { text-align: left; font-weight: 600; color: #1f2937; }
    .emi-amort tr:last-child td { border-bottom: none; }
    .emi-amort tr:nth-child(even) td { background: #f9fafb; }
    .amort-tip-wrap:hover .amort-tip { display: block !important; }
    @media (min-width: 640px) and (max-width: 1023px) {
      .hero-section { padding: 32px 40px 80px 40px !important; }
      .emi-seo { padding: 48px 40px 64px 40px !important; }
      .emi-card { padding: 28px !important; }
    }
    @media (max-width: 639px) {
      .emi-two-col { flex-direction: column !important; }
      .emi-chart-row { flex-direction: column !important; align-items: stretch !important; }
      .emi-chart-container { width: 100%; display: flex; justify-content: center; }
      .emi-card { padding: 20px !important; }
      .hero-section { padding: 32px 20px 60px 20px !important; }
      .emi-seo { padding: 32px 20px 48px 20px !important; }
    }
  `;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Onest:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{css}</style>

      <Navbar background="#1a4d3e" />

      {/* ── Hero Section ── */}
      <div className="hero-section" style={{ background: 'linear-gradient(180deg, #1a4d3e 0%, #2d6b5a 30%, #4a9d7f 60%, #7ec9a8 85%, #ffffff 100%)', padding: '32px 100px 80px 100px' }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: '18px' }}>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontWeight: 400 }}>Home</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontWeight: 400 }}>Calculators</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>EMI Calculator</span>
        </div>

        {/* Title + Share */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 className="landing-title" style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '62px', color: '#ffffff', margin: 0, lineHeight: 1 }}>
            EMI Calculator
          </h1>

          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              style={{ fontFamily: 'Onest, sans-serif', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#ffffff', fontWeight: 600, fontSize: '14px', paddingBottom: '8px' }}
            >
              <Share2 size={16} /> SHARE
            </button>
            {showShareMenu && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', padding: '12px', zIndex: 50, minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Onest', fontWeight: 600, fontSize: '13px', color: '#111827' }}>Share via</span>
                  <button onClick={() => setShowShareMenu(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {[
                    { id: 'whatsapp', label: 'WhatsApp', bg: '#25D366', icon: <svg width="20" height="20" fill="white" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
                    { id: 'twitter',  label: 'X',        bg: '#000',     icon: <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                    { id: 'copy',     label: copied ? 'Copied!' : 'Copy', bg: '#e5e7eb', icon: copied ? <Check size={18} color="#16a34a" /> : <Copy size={18} color="#374151" /> },
                  ].map(({ id, label, bg, icon }) => (
                    <button key={id} onClick={() => handleShare(id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
                      <span style={{ fontFamily: 'Onest', fontSize: '11px', color: '#6b7280' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Two-column layout ── */}
        <div className="emi-two-col" style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', minWidth: 0 }}>

          {/* LEFT — Calculator card */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="emi-card" style={{ backgroundColor: '#ffffff', borderRadius: '24px', boxShadow: '0 8px 48px rgba(0,0,0,0.18)' }}>

              {/* ── Loan type tabs ── */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '32px', flexWrap: 'wrap' }}>
                {Object.entries(LOAN_CONFIG).map(([key, val]) => (
                  <button
                    key={key}
                    onClick={() => handleLoanTypeSwitch(key)}
                    style={{
                      fontFamily: 'Onest, sans-serif', fontWeight: 600, fontSize: '14px',
                      padding: '9px 22px', borderRadius: '50px', cursor: 'pointer',
                      backgroundColor: loanType === key ? '#115e59' : '#f3f4f6',
                      color: loanType === key ? '#ffffff' : '#6b7280',
                      border: loanType === key ? 'none' : '1.5px solid #e5e7eb',
                      transition: 'all 0.18s ease',
                    }}
                  >
                    {val.label}
                  </button>
                ))}
              </div>

              {/* ── Loan Amount ── */}
              <SliderRow
                label="Loan Amount"
                value={sliderAmt}
                displayValue={amtStr}
                min={cfg.amtMin} max={cfg.amtMax} step={cfg.amtStep}
                displayPrefix="₹"
                minLabel={fmtC(cfg.amtMin)}
                maxLabel={fmtC(cfg.amtMax)}
                onSlider={(v) => setAmtStr(String(v))}
                onInput={handleAmtInput}
                sliderBg={sliderBg}
                inputWidth="90px"
                labelSt={labelSt} valueSt={valueSt} suffixSt={suffixSt} minMaxSt={minMaxSt} inputBoxSt={inputBoxSt}
              />

              {/* ── Interest Rate ── */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={labelSt}>Interest Rate (p.a.)</label>
                  <div style={inputBoxSt}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="no-spin"
                      value={rateStr}
                      onChange={(e) => handleRateInput(e.target.value)}
                      onBlur={() => {
                        const n = Math.min(Math.max(parseFloat(rateStr) || cfg.rateMin, cfg.rateMin), cfg.rateMax);
                        setRateStr(String(n));
                      }}
                      style={{ ...valueSt, width: '48px' }}
                    />
                    <span style={suffixSt}>%</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="emi-slider"
                  min={cfg.rateMin} max={cfg.rateMax} step={0.1}
                  value={interestRate}
                  onChange={(e) => setRateStr(String(parseFloat(e.target.value)))}
                  style={{ background: sliderBg(interestRate, cfg.rateMin, cfg.rateMax) }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', marginBottom: '10px' }}>
                  <span style={minMaxSt}>{cfg.rateMin}%</span>
                  <span style={minMaxSt}>{cfg.rateMax}%</span>
                </div>
                {/* Rate presets */}
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {cfg.ratePresets.map((preset) => {
                    const active = Math.abs(interestRate - preset) < 0.01;
                    return (
                      <button
                        key={preset}
                        onClick={() => setRateStr(String(preset))}
                        style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', fontWeight: 600, padding: '4px 13px', borderRadius: '50px', border: active ? 'none' : '1.5px solid #e5e7eb', cursor: 'pointer', backgroundColor: active ? '#115e59' : '#f9fafb', color: active ? '#ffffff' : '#6b7280', transition: 'all 0.15s ease' }}
                      >
                        {preset}%
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Tenure ── */}
              <SliderRow
                label="Loan Tenure"
                value={tenure}
                min={cfg.tenureMin} max={cfg.tenureMax} step={1}
                displaySuffix="Yr" inputWidth="40px"
                minLabel={`${cfg.tenureMin} Yr`}
                maxLabel={`${cfg.tenureMax} Yr`}
                onSlider={setTenure}
                onInput={handleTenure}
                sliderBg={sliderBg}
                isLast
                labelSt={labelSt} valueSt={valueSt} suffixSt={suffixSt} minMaxSt={minMaxSt} inputBoxSt={inputBoxSt}
              />

              {/* Divider */}
              <div style={{ borderTop: '1.5px solid #f3f4f6', margin: '32px 0' }} />

              {/* ── Chart + Results ── */}
              <div className="emi-chart-row" style={{ display: 'flex', alignItems: 'center', gap: '36px' }}>

                {/* Donut */}
                <div className="emi-chart-container">
                  <svg viewBox="0 0 200 200" width="180" height="180">
                    <g transform={`rotate(-90, ${CX}, ${CY})`}>
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#f3f4f6" strokeWidth="28" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#115e59" strokeWidth="28"
                        strokeDasharray={`${prinDash} ${circ}`} strokeLinecap="butt" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#4ade80" strokeWidth="28"
                        strokeDasharray={`${intDash} ${circ}`} strokeDashoffset={-(prinDash + gap)} strokeLinecap="butt" />
                    </g>
                    <text x={CX} y={CY - 6} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', fill: '#9ca3af', fontWeight: 500 }}>EMI</text>
                    <text x={CX} y={CY + 14} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', fill: '#111827', fontWeight: 700 }}>
                      {emi >= 100000 ? `₹${(emi / 100000).toFixed(1)}L` : emi >= 1000 ? `₹${(emi / 1000).toFixed(1)}K` : `₹${emi}`}
                    </text>
                  </svg>
                </div>

                {/* Results */}
                <div style={{ flex: 1 }}>
                  {[
                    { label: 'Principal Amount', value: loanAmount,    dot: '#115e59' },
                    { label: 'Total Interest',   value: totalInterest, dot: '#4ade80' },
                  ].map(({ label, value, dot }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: dot, flexShrink: 0 }} />
                        <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>{label}</span>
                      </div>
                      <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 700, color: '#111827' }}>{fmt(value)}</span>
                    </div>
                  ))}

                  <div style={{ borderTop: '1px solid #f3f4f6', margin: '16px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#111827', fontWeight: 600 }}>Your Monthly EMI</span>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '22px', fontWeight: 800, color: '#115e59', letterSpacing: '-0.02em' }}>{fmt(emi)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Total Payment</span>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 600, color: '#374151' }}>{fmt(totalAmount)}</span>
                  </div>

                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.5 }}>
                    {fmt(loanAmount)} · {interestRate}% p.a. · {tenure} {tenure === 1 ? 'year' : 'years'}
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1.5px solid #f3f4f6', margin: '32px 0' }} />

              {/* ── Amortization Details ── */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '17px', color: '#1f2937', margin: 0 }}>
                      Your Amortization Details
                    </h3>
                    <div style={{ position: 'relative', display: 'inline-flex' }} className="amort-tip-wrap">
                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default', flexShrink: 0 }}>
                        <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', fontWeight: 700, color: '#6b7280' }}>?</span>
                      </div>
                      <div className="amort-tip" style={{ display: 'none', position: 'absolute', left: '26px', top: '50%', transform: 'translateY(-50%)', backgroundColor: '#1f2937', color: '#fff', fontFamily: 'Onest, sans-serif', fontSize: '13px', lineHeight: 1.5, padding: '10px 14px', borderRadius: '10px', width: '240px', zIndex: 10, pointerEvents: 'none' }}>
                        A month-by-month (or year-by-year) breakdown of each payment — showing how much goes toward paying off the loan vs. how much is interest.
                        <span style={{ position: 'absolute', right: '100%', top: '50%', transform: 'translateY(-50%)', borderWidth: '6px', borderStyle: 'solid', borderColor: 'transparent #1f2937 transparent transparent' }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['yearly', 'monthly'].map((v) => (
                      <button
                        key={v}
                        onClick={() => setAmortView(v)}
                        style={{
                          fontFamily: 'Onest, sans-serif', fontSize: '13px', fontWeight: 600,
                          padding: '6px 18px', borderRadius: '50px', cursor: 'pointer',
                          backgroundColor: amortView === v ? '#115e59' : '#f3f4f6',
                          color: amortView === v ? '#ffffff' : '#6b7280',
                          border: amortView === v ? 'none' : '1.5px solid #e5e7eb',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {v.charAt(0).toUpperCase() + v.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                  <table className="emi-amort" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ textAlign: 'left' }}>{amortView === 'yearly' ? 'Year' : 'Month'}</th>
                        <th>Opening Balance</th>
                        <th>Principal</th>
                        <th>Interest</th>
                        {amortView === 'yearly' && <th>Total Payment</th>}
                        <th>Closing Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amortData.map((row) => (
                        <tr key={amortView === 'yearly' ? row.year : row.month}>
                          <td>{amortView === 'yearly' ? `Year ${row.year}` : `Month ${row.month}`}</td>
                          <td>{fmt(row.opening)}</td>
                          <td style={{ color: '#115e59', fontWeight: 600 }}>{fmt(row.principal)}</td>
                          <td style={{ color: '#6b7280' }}>{fmt(row.interest)}</td>
                          {amortView === 'yearly' && <td>{fmt(row.totalPayment)}</td>}
                          <td>{fmt(row.closing)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>

          {/* RIGHT — Sidebar */}
          <SmartCalculatorsSidebar currentSlug="emi" rightOffset="100px" />
        </div>
      </div>

      {/* ── SEO Section ── */}
      <div className="emi-seo">

        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '36px', color: '#1f2937', marginBottom: '16px' }}>
            EMI Calculator – Calculate Loan EMI for Home, Car & Personal Loans
          </h2>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            The Kapitalised EMI Calculator helps you instantly calculate your monthly loan instalments for any type of loan — home loan, car loan, or personal loan. Enter your loan amount, interest rate, and tenure to see your exact EMI, total interest payable, and a complete amortization schedule.
          </p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59', color: '#ffffff', padding: '14px 32px', borderRadius: '8px', fontWeight: 600, fontSize: '15px', border: 'none', cursor: 'pointer' }}>
            Calculate Your EMI
          </button>
        </div>

        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>What is an EMI?</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            EMI stands for Equated Monthly Instalment — a fixed amount you pay to your lender every month until your loan is fully repaid. Each EMI consists of two parts: the principal (the original loan amount) and the interest (the cost of borrowing). In the early months, a larger portion of your EMI goes toward interest. Over time, the principal portion increases.
          </p>
        </div>

        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>EMI Formula</h3>
          <div style={{ backgroundColor: '#115e59', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
            <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '20px', fontStyle: 'italic' }}>
              EMI = P &times; r &times; (1 + r)ⁿ / [(1 + r)ⁿ &minus; 1]
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[['P', 'Loan principal amount'], ['r', 'Monthly interest rate (Annual rate ÷ 12 ÷ 100)'], ['n', 'Loan tenure in months'], ['EMI', 'Equated Monthly Instalment']].map(([sym, desc]) => (
                <div key={sym} style={{ backgroundColor: 'rgba(255,255,255,0.12)', padding: '10px 14px', borderRadius: '8px' }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, color: '#4ade80', fontSize: '16px' }}>{sym}</span>
                  <span style={{ fontFamily: 'Onest, sans-serif', color: 'rgba(255,255,255,0.85)', fontSize: '14px', marginLeft: '8px' }}>= {desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '48px', backgroundColor: '#fff7ed', padding: '24px', borderRadius: '16px', border: '1px solid #fed7aa' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '18px', color: '#92400e', marginBottom: '12px', marginTop: 0 }}>Important Note</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#78350f', lineHeight: 1.7, margin: 0 }}>
            The EMI calculated here is based on the reducing balance method and assumes a fixed interest rate throughout the loan tenure. Actual EMI may vary based on your lender's processing fees, GST, insurance charges, and any changes in floating interest rates. Please consult your lender for the exact repayment schedule.
          </p>
        </div>

      </div>

    </div>
  );
};

// ── Reusable slider row (same as SIP) ────────────────────────────────────────
const SliderRow = ({
  label, value, displayValue, min, max, step,
  displayPrefix, displaySuffix, inputWidth = '72px',
  minLabel, maxLabel,
  onSlider, onInput, sliderBg, isLast,
  labelSt, valueSt, suffixSt, minMaxSt, inputBoxSt,
}) => (
  <div style={{ marginBottom: isLast ? 0 : '30px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
      <label style={labelSt}>{label}</label>
      <div style={inputBoxSt}>
        {displayPrefix && <span style={suffixSt}>{displayPrefix}</span>}
        <input
          type="text"
          inputMode="numeric"
          className="no-spin"
          value={displayValue !== undefined ? displayValue : value}
          onChange={(e) => onInput(e.target.value)}
          style={{ ...valueSt, width: inputWidth }}
        />
        {displaySuffix && <span style={suffixSt}>{displaySuffix}</span>}
      </div>
    </div>
    <input
      type="range"
      className="emi-slider"
      min={min} max={max} step={step}
      value={value}
      onChange={(e) => onSlider(step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))}
      style={{ background: sliderBg(value, min, max) }}
    />
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
      <span style={minMaxSt}>{minLabel}</span>
      <span style={minMaxSt}>{maxLabel}</span>
    </div>
  </div>
);

export default EMICalculator;
